import { format } from 'date-fns'
import { useContext, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'

import { Body2 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { formatDate } from '@utils/date.utils'

import { SubscriptionRequestTypeEnum } from '@model/SubscriptionRequestTypeEnum'
import { Subscription, isCancellationRequested } from '@model/SubscriptionTypes'
import { SubscriptionTimeInForce } from '@model/UpdateSubscriptionRequest'

import {
  getPendingAddNewSubscriptionLevel,
  getPendingCancelSubscriptionLevel,
  getPendingUpdateSubscriptionLevel,
} from './subscriptionUtils'

interface SubscriptionDetailMessageProps {
  selectedSubscription: Subscription | undefined
  onSetPendingUnsubscriptionInfoModal: (value: boolean) => void
}

export const SubscriptionDetailMessage: React.FC<SubscriptionDetailMessageProps> = ({
  selectedSubscription,
  onSetPendingUnsubscriptionInfoModal,
}) => {
  const { t } = useTranslation()
  const [auth] = useContext(AuthSessionContext)
  const dateFormat = auth?.dateFormatType.name

  const pendingSubscriptionLevel = useMemo(
    () =>
      getPendingCancelSubscriptionLevel(selectedSubscription) ||
      getPendingUpdateSubscriptionLevel(selectedSubscription) ||
      getPendingAddNewSubscriptionLevel(selectedSubscription),
    [selectedSubscription]
  )

  if (!pendingSubscriptionLevel || !pendingSubscriptionLevel.subscriptionRequest) {
    return null
  }

  const { subscriptionRequest, name } = pendingSubscriptionLevel
  const { type, dueDate } = subscriptionRequest || {}
  const isSubscriptionStartingToday =
    format(new Date(), dateFormat || 'yyyy-MM-dd') ===
    formatDate(new Date().toString(), { formatType: dateFormat })

  if (isSubscriptionStartingToday) {
    return null
  }

  if (isCancellationRequested(subscriptionRequest)) {
    return (
      <>
        <Body2 color='text-success-high-em' className='pl-1'>
          {t('Subscriptions.Active until', {
            date: formatDate(dueDate, { formatType: dateFormat }),
          })}
        </Body2>
        <div className='is-flex mt-1'>
          <Body2 className='has-text-warning'>{t('Subscriptions.Pending Unsubscription')}</Body2>
          <ActionIcon
            icon={InfoIcon}
            size='x20'
            onClick={() => {
              onSetPendingUnsubscriptionInfoModal(true)
            }}
          />
        </div>
      </>
    )
  } else if (type.name === SubscriptionRequestTypeEnum.UpdateExistingSubscription) {
    return (
      <p className='has-text-warning'>
        <Body2>
          {`${name} ${t('Subscriptions.Starting', {
            firstDateOfNextMonth: formatDate(dueDate, { formatType: dateFormat }),
          })}`}
        </Body2>
      </p>
    )
  } else if (
    type.name === SubscriptionRequestTypeEnum.AddNewSubscription ||
    subscriptionRequest?.subscriptionTimeInForce?.id ===
      SubscriptionTimeInForce.BeginningOfNextMonth
  ) {
    return (
      <p className='has-text-warning'>
        <Body2>
          {t('Subscriptions.Starting', {
            firstDateOfNextMonth: formatDate(dueDate, { formatType: dateFormat }),
          })}
        </Body2>
      </p>
    )
  }

  return null
}
